import * as React from 'react'

import {
  IconThumbsDown,
  InlineEditTextField,
  type InlineEditTextFieldStatus,
  Space,
  WithTooltip,
} from '@procurify/ui'

import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import messages from './messages'

export interface IInlineItemCostCellProps {
  item: CreditCardItem
  /** Inline-Edit iron law: View owns no state — value/status from the presenter hook */
  inputValue: string
  status: InlineEditTextFieldStatus
  onInputChange: (value: string) => void
  onInputBlur: () => void
  onInputKeyDown: (event: React.KeyboardEvent) => void
  onCurrencyChange: (currency: Currency) => void
}

export const InlineItemCostCell = ({
  item,
  inputValue,
  status,
  onInputChange,
  onInputBlur,
  onInputKeyDown,
  onCurrencyChange,
}: IInlineItemCostCellProps) => {
  const intl = useProcurifyIntl()

  return (
    <Space direction='horizontal' size='small' align='center'>
      {/* Red change flag when cost/currency diverge from original (BHV-038) */}
      {item.hasCostChange && item.unitCostDiffLog && (
        <WithTooltip
          title={intl.formatMessage(messages.costChangeTooltip, {
            originalCost: item.unitCostDiffLog.originalUnitCost.toFixed(2),
            currency: item.unitCostDiffLog.originalCurrencyLabel,
          })}
        >
          <WithTooltip.Wrapper>
            <IconThumbsDown
              color='danger'
              size={16}
              ariaLabel={intl.formatMessage(messages.costChangeAriaLabel)}
            />
          </WithTooltip.Wrapper>
        </WithTooltip>
      )}
      <Space.Item width={120}>
        <InlineEditTextField
          value={inputValue}
          status={status}
          disabled={status === 'pending'}
          onChange={onInputChange}
          onBlur={onInputBlur}
          onKeyDown={onInputKeyDown}
          ariaLabel={intl.formatMessage(messages.totalCostAriaLabel, {
            description: item.descriptionLabel,
          })}
          statusAriaLabels={{
            idle: intl.formatMessage(messages.statusIdleAriaLabel),
            pending: intl.formatMessage(messages.statusPendingAriaLabel),
            resolved: intl.formatMessage(messages.statusResolvedAriaLabel),
            rejected: intl.formatMessage(messages.statusRejectedAriaLabel),
          }}
        />
      </Space.Item>
      <Space.Item minWidth={110}>
        <CurrencySelect
          aria-label={intl.formatMessage(messages.currencyAriaLabel, {
            description: item.descriptionLabel,
          })}
          value={item.currency}
          isDisabled={status === 'pending'}
          onChange={(currency: Currency) => onCurrencyChange(currency)}
        />
      </Space.Item>
    </Space>
  )
}

InlineItemCostCell.displayName = 'InlineItemCostCell'
